import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Loader2, Headphones, Drum, Guitar, Piano, Mic, Music2, Gauge, KeyRound, Timer, Volume2, Repeat, ListMusic, FileText } from "lucide-react";
import { startTrack, stopTrack, setTempo, keyName, keyPc, barChords, setBandVolume, setClickVolume } from "@/lib/playalongEngine";
import { STYLES } from "@/data/playalongStyles";
import { BANDS, TRACKS, hasMelody } from "@/data/playalong";
import { loadTrackOverrides } from "@/lib/trackOverrides";
import { RecordTake } from "@/components/RecordTake";
import { api, downloadWorksheet } from "@/lib/api";
import { PlayAlongScore } from "@/components/PlayAlongScore";
import { StudioBand } from "@/components/StudioBand";


const PART_META = { drums: ["Drums", Drum], bass: ["Bass", Guitar], keys: ["Piano", Piano], guitar: ["Guitar", Guitar], lead: ["Lead", Mic] };

export const PlayAlong = ({ entry, instrumentId, lessonId, courseId, lessonLabel, preview = false }) => {
  const [trackId, setTrackId] = useState(entry.t);
  const [, refresh] = useState(0);
  useEffect(() => { loadTrackOverrides().then(() => { refresh((x) => x + 1); setBpm(entry.bpm || TRACKS[entry.t].bpm); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const track = TRACKS[trackId];
  const own = trackId === entry.t;
  const focus = own ? entry.focus : trackId === entry.also?.t ? entry.also.focus : `Free practice: play along with "${track.title}" and apply what this lesson teaches.`;
  const band = BANDS[instrumentId] || BANDS.singers;
  const style = STYLES[track.style];
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bar, setBar] = useState(-1);
  const [pos, setPos] = useState({ bar: -1, step: -1 });
  const [showScore, setShowScore] = useState(true);
  const baseBpm = (own && entry.bpm) || track.bpm;
  const [loop, setLoop] = useState(null);
  const [start, setStart] = useState(null);
  const practiced = useRef(0);
  const [bpm, setBpm] = useState(baseBpm);
  const [semi, setSemi] = useState(0);
  const [lead, setLead] = useState(entry.lead && band.leads.includes(entry.lead) ? entry.lead : band.leads[0]);
  const [parts, setParts] = useState(() => Object.fromEntries(band.parts.map((p) => [p, !(p === "lead" && band.leadOff)])));
  const [metro, setMetro] = useState(false);
  const [bandVol, setBandVol] = useState(100);
  const [clickVol, setClickVol] = useState(80);
  const opts = useRef({});
  opts.current.parts = parts;
  opts.current.metronome = metro;
  opts.current.loop = loop;
  const pickStart = (p) => {
    const s = loop && (p.bar < loop.start || p.bar > loop.end) ? { bar: loop.start, step: 0 } : p;
    setStart(s);
    if (playing) play({ start: s });
  };

  const flush = () => {
    const secs = practiced.current;
    if (secs >= 5 && lessonId) api.post("/practice/log", { lesson_id: lessonId, seconds: Math.min(secs, 3600) }).catch(() => {});
    practiced.current = 0;
  };
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => { practiced.current += 1; if (practiced.current >= 60) flush(); }, 1000);
    return () => { clearInterval(t); flush(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);
  useEffect(() => () => stopTrack(), []);

  const tapBar = (i) => {
    if (!loop || loop.end !== loop.start) setLoop({ start: i, end: i });
    else if (i >= loop.start) setLoop({ start: loop.start, end: i });
    else setLoop({ start: i, end: loop.start });
  };
  const pickTrack = (id) => { stop(); setTrackId(id); setBpm(id === entry.t && entry.bpm ? entry.bpm : TRACKS[id].bpm); setLoop(null); setSemi(0); setStart(null); };

  const play = async (over = {}) => {
    setLoading(true);
    try {
      Object.assign(opts.current, { bpm, semitones: semi, lead, start, onBar: setBar, onStep: (b, st) => setPos({ bar: b, step: st }), bandVolume: (bandVol / 100) * 0.9, clickVolume: clickVol / 100, ...over });
      await startTrack(track, opts.current);
      setPlaying(true);
    } finally { setLoading(false); }
  };
  const stop = () => { stopTrack(); setPlaying(false); setBar(-1); setPos({ bar: -1, step: -1 }); };
  const restartWith = (over) => { if (playing) play(over); };

  const keyRoot = (keyPc(track.key) + semi + 12) % 12;
  const leadLabel = hasMelody(track) ? (instrumentId === "singers" ? "Guide melody" : "Melody") : "Lead";

  return (
    <section className="mt-12 rounded-3xl border border-primary/40 bg-card p-6 sm:p-8" data-testid="playalong">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Headphones size={14} /> Play-along · {entry.minutes || 8} minutes</p>
      <h2 className="mt-2 font-serif text-2xl" data-testid="playalong-title">{track.title}</h2>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        {[track.genre, style.label, style.meter, `Key of ${keyName(keyRoot)}`, track.song ? "Song" : "Groove"].map((t, i) => (
          <span key={`${i}-${t}`} className="rounded-full bg-secondary px-3 py-1 text-muted-foreground">{t}</span>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{band.label}</p>
      <p className="mt-4 rounded-xl bg-primary/10 p-4 text-sm leading-relaxed text-foreground" data-testid="playalong-focus"><strong className="text-primary">Your part: </strong>{focus}</p>
      {trackId === "ode" && instrumentId === "pianists" && !preview && (
        <button type="button" onClick={() => downloadWorksheet(6, "chords")} data-testid="playalong-ode-chart"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-4 py-2 text-xs font-semibold text-primary transition-transform hover:scale-105">
          <FileText size={13} /> Print the Ode to Joy chord chart (left hand + chords)
          <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary-foreground">Included with your course</span>
        </button>
      )}

      {entry.also && !preview && (
        <button type="button" onClick={() => pickTrack(trackId === entry.also.t ? entry.t : entry.also.t)} data-testid="playalong-also"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
          <ListMusic size={13} /> {trackId === entry.also.t ? `Back to ${TRACKS[entry.t].title}` : `${entry.also.label}: ${TRACKS[entry.also.t].title}`}
        </button>
      )}

      {!preview && <label className="mt-4 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <ListMusic size={14} className="text-primary" /> Track:
        <select value={trackId} onChange={(e) => pickTrack(e.target.value)} data-testid="playalong-track-select"
          className="w-full max-w-full rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none sm:w-auto">
          {Object.entries(TRACKS).map(([id, t]) => <option key={id} value={id}>{`${id === entry.t ? "★ " : ""}${t.title} — ${t.genre}`}</option>)}
        </select>
      </label>}
      {!preview && <StudioBand trackId={trackId} track={track} beats={style.beats} interactivePlaying={playing} onPlayingChange={(p) => { if (p) stop(); }} />}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Repeat size={13} className="text-primary" />
          {loop ? <span data-testid="playalong-loop-label">Looping bars {loop.start + 1}–{loop.end + 1}</span> : "Tap a start bar, then an end bar, to loop a section"}
        </span>
        {loop && <button onClick={() => setLoop(null)} data-testid="playalong-loop-clear" className="font-semibold text-primary hover:underline">Loop whole track</button>}
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-8" data-testid="playalong-chart">
        {track.bars.map((b, i) => {
          const inLoop = loop && i >= loop.start && i <= loop.end;
          return (
            <button key={i} type="button" onClick={() => tapBar(i)} data-testid={`playalong-bar-${i}`}
              className={`rounded-lg border px-2 py-3 text-center font-mono text-xs transition-colors ${bar === i ? "border-primary bg-primary text-primary-foreground" : inLoop ? "border-primary/60 bg-primary/15 text-foreground" : "border-border bg-background text-foreground hover:border-primary/50"}`}>
              {barChords(b, keyRoot).map((c) => c.label).join(" ")}
            </button>
          );
        })}
      </div>

      <button onClick={() => setShowScore(!showScore)} data-testid="playalong-score-toggle" className="mt-4 text-xs font-semibold text-primary hover:underline">
        {showScore ? "Hide score" : "Show score"}
      </button>
      {showScore && (
        <>
          <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {start ? <span data-testid="playalong-start-label">▶ Playback starts from bar {start.bar + 1}{start.step ? ", mid-bar" : ""}</span> : <span>Tap any note or bar in the score to start playback from there.</span>}
            {start && <button onClick={() => setStart(null)} data-testid="playalong-start-clear" className="font-semibold text-primary hover:underline">Start from the top</button>}
          </p>
          <PlayAlongScore track={track} instrumentId={instrumentId} semitones={semi} pos={pos} start={start} onPick={pickStart} />
        </>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {!playing ? (
          <button onClick={() => play()} disabled={loading} data-testid="playalong-play"
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />} {loading ? "Loading band…" : "Play (1-bar count-in)"}
          </button>
        ) : (
          <button onClick={stop} data-testid="playalong-stop" className="flex items-center gap-2 rounded-full bg-foreground px-6 py-3 font-semibold text-background">
            <Square size={16} /> Stop
          </button>
        )}
        <button onClick={() => setMetro(!metro)} data-testid="playalong-metronome" aria-pressed={metro}
          className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${metro ? "bg-primary/15 text-primary ring-1 ring-primary/40" : "bg-secondary text-muted-foreground"}`}>
          <Timer size={15} /> Metronome {metro ? "on" : "off"}
        </button>
        <span className="text-xs text-muted-foreground">Loops until you stop it</span>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground"><Volume2 size={14} /> Track volume: <strong className="text-foreground" data-testid="playalong-band-volume-value">{bandVol}%</strong></span>
          <input type="range" min={0} max={100} value={bandVol} data-testid="playalong-band-volume"
            onChange={(e) => { const v = Number(e.target.value); setBandVol(v); opts.current.bandVolume = (v / 100) * 0.9; setBandVolume((v / 100) * 0.9); }}
            className="mt-2 w-full accent-[hsl(var(--primary))]" />
        </label>
        <label className="block text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground"><Timer size={14} /> Metronome volume: <strong className="text-foreground" data-testid="playalong-click-volume-value">{clickVol}%</strong></span>
          <input type="range" min={0} max={100} value={clickVol} data-testid="playalong-click-volume"
            onChange={(e) => { const v = Number(e.target.value); setClickVol(v); opts.current.clickVolume = v / 100; setClickVolume(v / 100); }}
            className="mt-2 w-full accent-[hsl(var(--primary))]" />
        </label>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground"><Gauge size={14} /> Tempo: <strong className="text-foreground" data-testid="playalong-bpm">{bpm} bpm</strong></span>
          <input type="range" min={Math.round(baseBpm * 0.5)} max={Math.round(baseBpm * 1.3)} value={bpm} data-testid="playalong-tempo"
            onChange={(e) => { const v = Number(e.target.value); setBpm(v); opts.current.bpm = v; if (playing) setTempo(v); }}
            className="mt-2 w-full accent-[hsl(var(--primary))]" />
        </label>
        <label className="block text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground"><KeyRound size={14} /> Key / pitch: <strong className="text-foreground" data-testid="playalong-key">{keyName(keyRoot)} ({semi > 0 ? `+${semi}` : semi} semitones)</strong></span>
          <input type="range" min={-6} max={6} value={semi} data-testid="playalong-pitch"
            onChange={(e) => { const v = Number(e.target.value); setSemi(v); restartWith({ semitones: v }); }}
            className="mt-2 w-full accent-[hsl(var(--primary))]" />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="playalong-parts">
        {band.parts.map((p) => {
          const [label, Icon] = PART_META[p];
          return (
            <button key={p} onClick={() => setParts({ ...parts, [p]: !parts[p] })} data-testid={`playalong-part-${p}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${parts[p] ? "bg-primary/15 text-primary ring-1 ring-primary/40" : "bg-secondary text-muted-foreground line-through"}`}>
              <Icon size={14} /> {p === "lead" ? leadLabel : label}
            </button>
          );
        })}
        {band.leads.length > 1 && (
          <div className="ml-auto flex rounded-full bg-secondary p-1 text-xs" data-testid="playalong-lead-select">
            {band.leads.map((l) => (
              <button key={l} onClick={() => { setLead(l); restartWith({ lead: l }); }} data-testid={`playalong-lead-${l}`}
                className={`flex items-center gap-1 rounded-full px-3 py-1.5 capitalize ${lead === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {l === "sax" ? <Music2 size={12} /> : <Mic size={12} />} {l}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="mt-6 text-[11px] text-muted-foreground" data-testid="playalong-credits">Interactive band sounds: Virtuosity Drums (CC0) and Salamander Grand Piano by Alexander Holm (CC-BY 3.0).</p>
      {!preview && <RecordTake playing={playing} onStartBand={() => play()} instrumentId={instrumentId} courseId={courseId} lessonLabel={lessonLabel} trackTitle={track.title} />}
    </section>
  );
};
